/**
 * Formulário "Dar Baixa na TARP" — 3 blocos fiéis ao protótipo V8
 * (1·Diagnóstico técnico / 2·Enquadramento ANM / 3·Plano de ação) + autorização.
 * Apresentação pura: estado e ação (Concluir) ficam no TarpDetailModal.
 */
import { DEV_AUTH_PIN, ENQUADRAMENTO_LABEL, ENQUADRAMENTOS, NE_TO_TARP_LEVEL, PARECER_MIN, type EnquadramentoAnm } from '@/domain';
import type { AcaoDraft } from '@/domain/planos/acoes';
import type { Plano } from '@/domain/planos';
import { AcoesEditor } from '@/components/plano/AcoesEditor';
import { LEVEL_UI } from '@/ui';
import { FIELD_CLS } from './Modal';

function Bloco({ num, titulo, children }: { num: number; titulo: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-jf-border p-3">
      <div className="mb-2 text-xs font-extrabold text-jf-yellow">
        {num} · {titulo}
      </div>
      {children}
    </div>
  );
}

/** Par de botões "Não se aplica" / "Sim …" (fiel aos ct-toggle do V8). */
function ToggleNaoSim({
  aplica,
  onChange,
  labelSim,
}: {
  aplica: boolean;
  onChange: (v: boolean) => void;
  labelSim: string;
}) {
  const cls = (ativo: boolean) =>
    `flex-1 rounded-lg border py-2 text-xs font-bold transition ${
      ativo ? 'border-jf-yellow bg-jf-yellow/15 text-jf-yellow' : 'border-jf-border text-jf-muted hover:text-white'
    }`;
  return (
    <div className="flex gap-2">
      <button type="button" className={cls(!aplica)} onClick={() => onChange(false)}>
        Não se aplica
      </button>
      <button type="button" className={cls(aplica)} onClick={() => onChange(true)}>
        {labelSim}
      </button>
    </div>
  );
}

export interface BaixaFormProps {
  parecer: string;
  onParecer: (v: string) => void;
  anmAplica: boolean;
  onAnmAplica: (v: boolean) => void;
  enquadramento: EnquadramentoAnm | null;
  onEnquadramento: (v: EnquadramentoAnm | null) => void;
  planoAplica: boolean;
  onPlanoAplica: (v: boolean) => void;
  acoes: AcaoDraft[];
  onAcoes: (v: AcaoDraft[]) => void;
  /** Data de abertura (hoje) — referência do prazo em dias úteis por ação. */
  abertura: string;
  /** Planos já vinculados à TARP (GUPM-213 Extra 2) — habilita "vincular existente". */
  planosExistentes?: Plano[];
  planoExistenteId?: string | null;
  onPlanoExistente?: (id: string | null) => void;
  totpCode: string;
  onTotpCode: (v: string) => void;
  /** GUPM-186: quando a barragem não está definida, o enquadramento ANM fica indisponível. */
  barragemDefinida?: boolean;
  /**
   * GUPM-295: motivos que mantêm "Concluir" bloqueado. Antes, `validateBaixa` alimentava
   * só o `disabled` do botão e o RT ficava sem saber o que faltava — o campo de prazo em
   * branco travava a baixa em silêncio.
   */
  errors?: string[];
}

export function DarBaixaForm({
  errors = [],
  parecer,
  onParecer,
  anmAplica,
  onAnmAplica,
  enquadramento,
  onEnquadramento,
  planoAplica,
  onPlanoAplica,
  acoes,
  onAcoes,
  abertura,
  planosExistentes = [],
  planoExistenteId = null,
  onPlanoExistente,
  totpCode,
  onTotpCode,
  barragemDefinida = true,
}: BaixaFormProps) {
  return (
    <div className="space-y-3">
      <Bloco num={1} titulo="Diagnóstico técnico">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[11px] text-jf-muted">Parecer técnico (obrigatório, mín. {PARECER_MIN}):</span>
          <span className={`text-[10px] ${parecer.trim().length >= PARECER_MIN ? 'text-level-normal' : 'text-jf-dim'}`}>
            {parecer.trim().length}/{PARECER_MIN}
          </span>
        </div>
        <textarea
          className={FIELD_CLS}
          rows={4}
          value={parecer}
          onChange={(e) => onParecer(e.target.value)}
          placeholder="Causa identificada, medidas adotadas e conclusão técnica…"
        />
      </Bloco>

      <Bloco num={2} titulo="Enquadramento em nível da ANM">
        {barragemDefinida ? (
          <>
            <p className="mb-2 text-[11px] text-jf-muted">
              A situação resulta em enquadramento de nível da estrutura (Res. ANM 220)?
            </p>
            <ToggleNaoSim
              aplica={anmAplica}
              onChange={(v) => {
                onAnmAplica(v);
                if (!v) onEnquadramento(null);
              }}
              labelSim="Sim, enquadrar"
            />
            {anmAplica && (
              <div className="mt-2 flex flex-wrap gap-2">
                {ENQUADRAMENTOS.map((ne) => {
                  // Cor do nível (design system): NE-1 Atenção (amarelo), NE-2 Alerta
                  // (laranja), NE-3 Emergência (vermelho). Selecionado = preenchido.
                  const lvl = LEVEL_UI[NE_TO_TARP_LEVEL[ne]];
                  const sel = enquadramento === ne;
                  return (
                    <button
                      key={ne}
                      type="button"
                      onClick={() => onEnquadramento(ne)}
                      aria-pressed={sel}
                      className="rounded-lg border px-3 py-1.5 text-xs font-bold transition"
                      style={{
                        color: lvl.color,
                        borderColor: sel ? lvl.color : lvl.border,
                        background: sel ? lvl.bg : 'transparent',
                        boxShadow: sel ? `inset 0 0 0 1px ${lvl.color}` : undefined,
                      }}
                    >
                      {ENQUADRAMENTO_LABEL[ne]}
                    </button>
                  );
                })}
              </div>
            )}
            {anmAplica && (
              <p className="mt-2 text-[10px] text-jf-dim">
                Enquadrar declara a estrutura em nível ANM — ato técnico do RT. Gera e-mail padrão às
                partes (EoR, RT, JF Brasil, TetraTech).
              </p>
            )}
          </>
        ) : (
          <p className="text-[11px] text-amber-300/90">
            ⚠ Defina a barragem antes de enquadrar em nível da ANM — a Res. 220 é sobre a
            estrutura. Você ainda pode dar baixa com parecer e plano de ação.
          </p>
        )}
      </Bloco>

      <Bloco num={3} titulo="Plano de ação">
        <p className="mb-2 text-[11px] text-jf-muted">A situação resulta em plano de ação?</p>
        <ToggleNaoSim aplica={planoAplica} onChange={onPlanoAplica} labelSim="Sim, definir ações" />
        {planoAplica && (
          <div className="mt-2 space-y-2">
            {planosExistentes.length > 0 && (
              <div>
                <label className="mb-1 block text-[11px] text-jf-muted">
                  Já existe plano para esta TARP — criar novo ou vincular a um existente:
                </label>
                <select
                  className={FIELD_CLS}
                  aria-label="Vincular a plano existente"
                  value={planoExistenteId ?? ''}
                  onChange={(e) => onPlanoExistente?.(e.target.value || null)}
                >
                  <option value="">— Criar novo plano —</option>
                  {planosExistentes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.codigo} · {p.acoes.length} ação/ações — {p.titulo}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {planoExistenteId ? (
              <p className="text-[10px] text-jf-dim">
                A baixa será vinculada ao plano selecionado — nenhuma ação nova é criada.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {/* flat: sem card por ação (o Bloco 3 já é a seção) — campos escuros direto,
                    igual ao parecer. Evita "card dentro de card" (GUPM-213). */}
                <AcoesEditor acoes={acoes} onChange={onAcoes} abertura={abertura} flat />
                <p className="col-span-2 text-[10px] text-jf-dim">
                  As ações são consolidadas no módulo de Planos de Ação, vinculadas a esta TARP.
                </p>
              </div>
            )}
          </div>
        )}
      </Bloco>

      {/* Autorização — no lugar do checkbox de confirmação do V8. */}
      <div className="rounded-lg border border-level-normal/40 bg-level-normal/[0.06] p-3">
        <label className="mb-1 block text-xs font-extrabold text-level-normal">
          Autorização — código TOTP
        </label>
        <input
          className={FIELD_CLS}
          inputMode="numeric"
          autoComplete="one-time-code"
          value={totpCode}
          onChange={(e) => onTotpCode(e.target.value)}
          placeholder="000000"
        />
        <p className="mt-1 text-[10px] text-jf-dim">
          Confirma a análise técnica e autoriza o encerramento. Em produção, o TOTP é obrigatório.
          {import.meta.env.DEV && (
            <>
              {' '}
              Em ambiente de teste use o PIN <span className="font-bold">{DEV_AUTH_PIN}</span> (baixa
              registrada como não-verificada).
            </>
          )}
        </p>
      </div>

      {errors.length > 0 && (
        <div
          role="alert"
          className="rounded-lg border border-amber-400/40 bg-amber-400/[0.08] p-3 text-[11px] text-amber-200"
        >
          <p className="mb-1 font-extrabold">Falta para concluir a baixa:</p>
          <ul className="list-disc space-y-0.5 pl-4">
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
